"use client";

import * as React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

function Table({ className, ...props }: React.ComponentProps<"table">) {
  const isMobile = useIsMobile();

  return (
    <div
      data-slot="table-container"
      className={cn(
        "relative w-full rounded",
        // Enhanced mobile/tablet responsive behavior
        "overflow-x-auto",
        // Improved scrolling on mobile
        "scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent",
        // Better mobile touch scrolling
        "[-webkit-overflow-scrolling:touch] scroll-smooth"
      )}
    >
      <table
        data-slot="table"
        className={cn(
          "w-full caption-bottom text-sm",
          // Mobile-specific table styling
          isMobile && "min-w-full ",
          className
        )}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead data-slot="table-header" className={cn("[&_tr]:border-b", className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return <tbody data-slot="table-body" className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  const isMobile = useIsMobile();

  return (
    <tr
      data-slot="table-row"
      className={cn(
        "group hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        // Enhanced mobile row styling
        isMobile && [
          "border-b-2 border-muted/30",
          "hover:bg-muted/30 active:bg-muted/40",
          "transition-all duration-200 ease-in-out",
        ],
        className
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  const isMobile = useIsMobile();

  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-muted-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5",
        // Responsive header styling
        isMobile
          ? [
              "h-12 px-3 py-2",
              "text-xs font-semibold uppercase tracking-wider",
              "bg-muted/50 border-b-2 border-muted",
              "sticky top-0 z-10",
            ]
          : ["h-10 px-4"],
        // Tablet adjustments
        "sm:h-11 sm:px-3 md:h-12 md:px-4",
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  const isMobile = useIsMobile();

  return (
    <td
      data-slot="table-cell"
      className={cn(
        "text-black dark:text-white",
        "align-middle overflow-hidden text-ellipsis whitespace-nowrap",

        // Base padding
        isMobile ? "px-3 py-2 text-sm" : "px-4 py-2 text-sm",
        // Breakpoint overrides
        "sm:px-3 sm:py-2 md:px-4 md:py-2",
        !isMobile && "h-13",
        className
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption data-slot="table-caption" className={cn("text-muted-foreground mt-4 text-sm", className)} {...props} />
  );
}

// Responsive Table Wrapper Component
function ResponsiveTable({
  children,
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?: "default" | "card" | "compact";
}) {
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "responsive-table-wrapper",
        // Base responsive table styles
        "w-full overflow-hidden rounded-lg border border-border bg-card",
        // Variant-specific styling
        variant === "card" && isMobile && "mobile-card-table",
        variant === "compact" && "compact-table",
        // Enhanced mobile styling
        isMobile && ["shadow-md", "border-2", "rounded-xl"],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// Mobile-friendly table utilities
const TableUtils = {
  // Helper to create mobile-friendly table headers with data labels
  createMobileHeaders: (headers: string[]) => {
    return headers.map((header, index) => (
      <TableHead key={index} data-label={header}>
        {header}
      </TableHead>
    ));
  },

  // Helper to create mobile-friendly table cells with data labels
  createMobileCells: (data: any[], headers: string[]) => {
    return data.map((item, rowIndex) => (
      <TableRow key={rowIndex}>
        {headers.map((header, cellIndex) => (
          <TableCell key={cellIndex} data-label={header}>
            {item[header.toLowerCase().replace(/\s+/g, "_")]}
          </TableCell>
        ))}
      </TableRow>
    ));
  },
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  ResponsiveTable,
  TableUtils,
};
